import React, { useState } from 'react';
import { ChevronDown, Accessibility, User, HelpCircle, Search, Menu, ChevronRight } from 'lucide-react';
import { Button } from './ui/Button';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from './ui/DropdownMenu';
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle } from './ui/Sheet';
import { Logo } from './Logo';
const NAVY = '#0f2d5c';
interface NavItem {
  label: string;
  dropdown?: string[];
}
const primaryLinks: NavItem[] = [{
  label: 'Home'
}, {
  label: 'About',
  dropdown: ['Overview', 'History', 'Vision & Mission', 'Leadership']
}, {
  label: 'Authority',
  dropdown: ['Board of Trustees', 'Syndicate', 'Academic Council']
}, {
  label: 'EWU Updates',
  dropdown: ['News', 'Events', 'Notices']
}];
const secondaryLeft: NavItem[] = [{
  label: 'Faculties',
  dropdown: ['Sciences & Engineering', 'Business', 'Liberal Arts']
}, {
  label: 'Departments',
  dropdown: ['CSE', 'EEE', 'BBA', 'English', 'Economics']
}, {
  label: 'Admission',
  dropdown: ['Undergraduate', 'Graduate', 'How to Apply']
}, {
  label: 'EWU Policies'
}];
const secondaryRight: NavItem[] = [{
  label: 'Research & Innovation',
  dropdown: ['Research Centres', 'Publications', 'Grants']
}, {
  label: 'IQAC'
}, {
  label: 'CRT'
}, {
  label: 'Library'
}];
function NavLink({
  item
}: {
  item: NavItem;
}) {
  if (!item.dropdown) {
    return <a href="#" className="whitespace-nowrap text-sm font-medium text-slate-700 transition-colors hover:text-[#0f2d5c]" data-id="element-103">
        {item.label}
      </a>;
  }
  return <DropdownMenu data-id="element-104">
      <DropdownMenuTrigger className="flex items-center gap-1 whitespace-nowrap text-sm font-medium text-slate-700 outline-none transition-colors hover:text-[#0f2d5c]" data-id="element-105">
        {item.label}
        <ChevronDown className="h-3.5 w-3.5" data-id="element-106" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-48" data-id="element-107">
        {item.dropdown.map(d => <DropdownMenuItem key={d} data-id="element-108">{d}</DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>;
}
function IconButton({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <button aria-label={label} className="flex h-9 w-9 items-center justify-center rounded-full text-slate-600 transition-colors hover:bg-slate-100 hover:text-[#0f2d5c]" data-id="element-109">
      {children}
    </button>;
}
export function Header() {
  const [open, setOpen] = useState(false);
  const allMobileLinks = [...primaryLinks, ...secondaryLeft, ...secondaryRight];
  return <header className="pointer-events-none absolute inset-x-0 top-0 z-40 px-3 pt-3 sm:px-6 sm:pt-5" data-id="element-110">
      <div className="pointer-events-auto relative mx-auto max-w-7xl rounded-2xl bg-white/60 shadow-lg ring-1 ring-black/5 backdrop-blur" data-id="element-111">
        {/* Center dipping logo badge (desktop) — shield/crest shape */}
        <div className="pointer-events-none absolute left-1/2 top-0 z-10 hidden -translate-x-1/2 lg:block" data-id="element-112">
          <div className="relative flex h-28 w-32 items-center justify-center bg-[#d9dadd] shadow-md [clip-path:path('M0_0_H128_V70_C128_92_100_108_64_120_C28_108_0_92_0_70_Z')]" data-id="element-113">
            <Logo className="mt-1 px-2" data-id="element-114" />
          </div>
        </div>

        {/* Desktop nav */}
        <div className="hidden lg:block" data-id="element-115">
          {/* Row 1 */}
          <div className="flex items-center justify-between px-6 pt-3" data-id="element-116">
            <nav className="flex items-center gap-6" data-id="element-117">
              {primaryLinks.map(item => <NavLink key={item.label} item={item} data-id="element-118" />)}
            </nav>
            <div className="flex items-center gap-2" data-id="element-119">
              <IconButton label="Accessibility" data-id="element-120">
                <Accessibility className="h-5 w-5" data-id="element-121" />
              </IconButton>
              <IconButton label="Account" data-id="element-122">
                <User className="h-5 w-5" data-id="element-123" />
              </IconButton>
              <IconButton label="Help" data-id="element-124">
                <HelpCircle className="h-5 w-5" data-id="element-125" />
              </IconButton>
              <IconButton label="Search" data-id="element-126">
                <Search className="h-5 w-5" data-id="element-127" />
              </IconButton>
              <Button size="sm" className="ml-1 gap-1 text-white" style={{
              backgroundColor: NAVY
            }} data-id="element-128">
                Quick Link
                <ChevronDown className="h-4 w-4" data-id="element-129" />
              </Button>
              <Button size="sm" className="gap-1 bg-[#b63a2f] text-white hover:bg-[#9f3127]" data-id="element-130">
                Apply Now
                <ChevronDown className="h-4 w-4" data-id="element-131" />
              </Button>
            </div>
          </div>
          {/* Row 2 */}
          <div className="flex items-center justify-between px-6 pb-3 pt-3" data-id="element-132">
            <nav className="flex items-center gap-6" data-id="element-133">
              {secondaryLeft.map(item => <NavLink key={item.label} item={item} data-id="element-134" />)}
            </nav>
            <nav className="flex items-center gap-6" data-id="element-135">
              {secondaryRight.map(item => <NavLink key={item.label} item={item} data-id="element-136" />)}
            </nav>
          </div>
        </div>

        {/* Mobile / tablet bar */}
        <div className="flex items-center justify-between px-4 py-3 lg:hidden" data-id="element-137">
          <div className="flex items-center gap-2" data-id="element-138">
            <div className="rounded-lg bg-white p-1" data-id="element-139">
              <Logo className="scale-90" data-id="element-140" />
            </div>
          </div>
          <div className="flex items-center gap-1.5" data-id="element-141">
            <IconButton label="Search" data-id="element-142">
              <Search className="h-5 w-5" data-id="element-143" />
            </IconButton>
            <Button size="sm" className="bg-[#b63a2f] text-white hover:bg-[#9f3127]" data-id="element-144">
              Apply Now
            </Button>
            <Sheet open={open} onOpenChange={setOpen} data-id="element-145">
              <SheetTrigger asChild data-id="element-146">
                <button aria-label="Open menu" className="flex h-9 w-9 items-center justify-center rounded-full text-slate-700 hover:bg-slate-100" data-id="element-147">
                  <Menu className="h-6 w-6" data-id="element-148" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80" data-id="element-149">
                <SheetHeader data-id="element-150">
                  <SheetTitle style={{
                  color: NAVY
                }} data-id="element-151">
                    Menu
                  </SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col px-4 pb-6" data-id="element-152">
                  {allMobileLinks.map(item => <a key={item.label} href="#" className="flex items-center justify-between border-b border-slate-100 py-3 text-sm font-medium text-slate-700" data-id="element-153">
                      {item.label}
                      {item.dropdown && <ChevronRight className="h-4 w-4 text-slate-400" data-id="element-154" />}
                    </a>)}
                  <Button className="mt-4 w-full text-white" style={{
                  backgroundColor: NAVY
                }} data-id="element-155">
                    Quick Link
                  </Button>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>;
}